import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useToast } from "@shared-kernel/primary/shared/shadcn/ui/use-toast";
import {
  UNAUTHORIZED_ERROR_NAME,
  SESSION_EXPIRED_ERROR_NAME,
  FORBIDDEN_ERROR_NAME,
  SESSION_UNAVAILABLE_ERROR_NAME,
} from "@shared-kernel/domain/errors/errors";
import { terminateSession } from "@user-management-context/write/application/use-cases/session/terminate-session";
import { resetAuthError } from "@user-management-context/write/application/use-cases/handle-auth-error/handle-auth-error";
import { selectAuthErrorHandling } from "@user-management-context/write/application/use-cases/handle-auth-error/selectors/auth-error-handling-selectors";

export const useAuthErrorRedirect = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const { error: authError } = useAppSelector(selectAuthErrorHandling);

  useEffect(() => {
    // Neither of these ends the session: say what happened and stay put. Reset
    // afterwards, otherwise an identical second occurrence would go unreported.
    if (authError === FORBIDDEN_ERROR_NAME) {
      toast({ description: "Accès non autorisé", variant: "destructive" });
      dispatch(resetAuthError());
      return;
    }

    // The server could not be reached (rate limit, 5xx, network). The session is
    // untouched and a later retry will walk straight back in — but without a word
    // the user only sees the login screen and believes they were signed out.
    if (authError === SESSION_UNAVAILABLE_ERROR_NAME) {
      toast({ description: "Connexion au serveur impossible. Réessayez dans un instant.", variant: "destructive" });
      dispatch(resetAuthError());
      return;
    }

    if (authError !== UNAUTHORIZED_ERROR_NAME && authError !== SESSION_EXPIRED_ERROR_NAME) return;

    // Impersonation tokens are access-only (no refresh) → an auth error ends the
    // session; the admin logs back in (backend-sanctioned "re-login"). Normal
    // sessions that reach here have already exhausted refresh (see the interceptor).
    if (authError === UNAUTHORIZED_ERROR_NAME) {
      toast({ description: "Accès non autorisé", variant: "destructive" });
    } else {
      toast({ description: "Session expirée", variant: "destructive" });
    }
    terminateSession();
    window.location.href = "/login";
  }, [authError, toast, dispatch]);
};
